import * as React from 'react';
import { ComponentPropsWithoutRef, useEffect, useImperativeHandle } from 'react';
import { usePreservedCallback } from '@toss/react';
import { cva } from 'class-variance-authority';
import { cn } from '@zep/utils';
const inputVariants = cva(['bg-background flex w-full resize-none text-sm !leading-normal placeholder:text-gray-500', 'px-[10px]', 'border focus:border-[#6758FF]', 'rounded-[8px] border-gray-300', 'focus-visible:outline-none',
// 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
'disabled:cursor-not-allowed', 'text-body-2 font-medium text-gray-900'], {
  variants: {
    size: {
      sm: ['max-h-[48px] min-h-[32px] py-[5px] disabled:bg-gray-100'],
      md: ['max-h-[38px] min-h-[38px] py-[8px] disabled:bg-gray-200']
    },
    type: {
      default: ['md:focus:shadow-[0px_0px_0px_1px_rgba(103,88,255,0.20),0px_0px_0px_4px_rgba(103,88,255,0.08)]'],
      ai: ['md:focus:shadow-[0px_0px_0px_1px_rgba(21,176,156,0.20),0px_0px_0px_4px_rgba(21,176,156,0.08)]']
    }
  },
  defaultVariants: {
    size: 'sm',
    type: 'default'
  }
});
export type ChatInputProps = ComponentPropsWithoutRef<'textarea'> & {
  initialHeight?: number;
  maxHeight?: number;
  size?: 'sm' | 'md';
  type?: 'default' | 'ai';
};
export const ChatInput = React.forwardRef<HTMLTextAreaElement, ChatInputProps>(({
  value,
  className,
  onChange,
  initialHeight = 32,
  maxHeight = 48,
  size,
  type,
  ...props
}, ref) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // @ts-expect-error
  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    ...textareaRef.current
  }));
  const adjustHeight = usePreservedCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = `${initialHeight}px`;
      const newHeight = Math.min(textareaRef.current.scrollHeight + 2, maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  });
  useEffect(() => {
    adjustHeight();
  }, [adjustHeight, value]);
  return <textarea className={cn(inputVariants({
    size: size,
    type: type
  }), className)} value={value} ref={textareaRef}
  // @ts-ignore
  enterKeyHint="send" onChange={e => {
    adjustHeight();
    if (onChange) onChange(e);
  }} {...props} />;
});
ChatInput.displayName = 'ChatInputTextArea';